import { RowSelectionState, ColumnDef } from '@tanstack/react-table'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { FbLink } from 'components/FbUI/FbLink'
import { Dispatch, SetStateAction, useMemo } from 'react'
import { useUnifiedSearchCompanyUrl } from 'utils/getUnifiedSearchCompanyUrl'
import { CSVRow, InternalColumns } from '../importer-types'
import { OppTypeBadge, getOppTypeLabel } from './OppTypeBadge'
import { useContainerHeight } from './useContainerHeight'

export function ImportWizardConfirmMatchedStep({
  show,
  headers,
  setRowSelection,
  rowSelection,
  data,
  loading,
}: {
  show: boolean
  headers: Record<string, string>
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>
  rowSelection: RowSelectionState
  data: CSVRow[]
  loading: boolean
}) {
  const { getCompanyUrlFromId } = useUnifiedSearchCompanyUrl()
  const [containerRef, containerHeight] = useContainerHeight()

  const columnDefs: ColumnDef<CSVRow>[] = useMemo(() => {
    return [
      {
        id: 'input_data',
        header: 'Input Data',
        accessorFn: (row) => {
          // Sort by input name
          return (row[headers.name] || '').toLowerCase()
        },
        cell: ({ row }) => {
          const assembledAddress = [
            row.original[headers.street],
            row.original[headers.city],
            row.original[headers.state],
            row.original[headers.zip],
          ]
            .filter(Boolean)
            .join(', ')

          const name = row.original[headers.name] || '-'
          const domain = row.original[headers.domain] || '-'
          const address =
            row.original[headers.full_address] || assembledAddress || '-'

          return (
            <div className="space-y-0.5 text-sm">
              <div className="text-gray-700">
                <span className="font-medium text-gray-500">Name:</span> {name}
              </div>
              <div className="text-gray-700">
                <span className="font-medium text-gray-500">Domain:</span>{' '}
                {domain}
              </div>
              <div className="text-gray-700">
                <span className="font-medium text-gray-500">Address:</span>{' '}
                {address}
              </div>
            </div>
          )
        },
      },
      {
        id: 'matched_data',
        header: 'Matched Data',
        accessorFn: (row) => {
          // Sort by matched name
          const chain = row[InternalColumns.MATCHED_CHAIN]
          const door = row[InternalColumns.MATCHED_DOOR]
          return (door?.name || chain?.name || '').toLowerCase()
        },
        cell: ({ row }) => {
          const chain = row.original.__internal__matched_chain
          const door = row.original.__internal__matched_door

          const name = door?.name || chain?.name || '-'
          const domain = door?.domain || chain?.domain || '-'
          const address = door?.address || '-'

          // Build company ID for link
          const companyId = door
            ? `${door.type}${door.item_id}`
            : chain
              ? `${chain.type}${chain.item_id}`
              : null
          const companyUrl = companyId ? getCompanyUrlFromId(companyId) : null

          return (
            <div className="space-y-0.5 text-sm">
              <div className="text-gray-700">
                <span className="font-medium text-gray-500">Name:</span>{' '}
                {companyUrl ? (
                  <FbLink to={companyUrl} target="_blank">
                    {name}
                  </FbLink>
                ) : (
                  name
                )}
              </div>
              <div className="text-gray-700">
                <span className="font-medium text-gray-500">Domain:</span>{' '}
                {domain}
              </div>
              <div className="text-gray-700">
                <span className="font-medium text-gray-500">Address:</span>{' '}
                {address}
              </div>
            </div>
          )
        },
      },
      {
        id: 'company_target_type',
        header: 'Company Target Type',
        accessorFn: (row) => {
          const chain = row[InternalColumns.MATCHED_CHAIN]
          const door = row[InternalColumns.MATCHED_DOOR]
          const typeCode = door?.type || chain?.type
          return getOppTypeLabel(typeCode) || ''
        },
        cell: ({ row }) => {
          const chain = row.original[InternalColumns.MATCHED_CHAIN]
          const door = row.original[InternalColumns.MATCHED_DOOR]
          const typeCode = door?.type || chain?.type
          if (!typeCode) return '-'
          return <OppTypeBadge typeCode={typeCode} />
        },
      },
    ]
  }, [headers, getCompanyUrlFromId])

  if (!show) {
    return null
  }

  return (
    <div ref={containerRef} className="h-full overflow-hidden">
      <ClientDataTable
        loading={loading}
        selectedRows={rowSelection}
        onRowSelectionChange={setRowSelection}
        data={data}
        height={containerHeight || 400}
        columns={columnDefs}
        enableSorting
        enableRowSelection
        invertedRowSelection
        selectionRowID={InternalColumns.COMPANY_HASH}
      />
    </div>
  )
}
